// src/enduser/eventdetail.jsx
// イベント詳細（イベントメニュー）。ログイン不要の公開APIを呼ぶ。
import React from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import "./eventdetail.css";

const API_URL = import.meta.env.VITE_API_URL;

/* ========================================
 * ヘルパー
 * ======================================== */
const pad2 = (n) => String(n).padStart(2, "0");

const DOW_EN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DOW_JP = ["日", "月", "火", "水", "木", "金", "土"];

// "YYYY-MM-DD" をローカル日付として扱う（new Date(iso) だとUTC扱いで日付がずれる恐れがある）
const parseDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatEyebrow = (d) =>
  `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} ${DOW_EN[d.getDay()]}`;

const formatDateJP = (d) =>
  `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${DOW_JP[d.getDay()]}）`;

// メニュー（遷移先は /events/:eventId/ の下）
const MENU = [
  { label: "リング", sub: "RINGS", path: "rings" },
  { label: "審査員", sub: "JUDGES", path: "judges" },
  { label: "犬種・グループ", sub: "BREEDS", path: "groups" },
];

/* ========================================
 * コンポーネント
 * ======================================== */
export default function EnduserEventDetail() {
  const { eventId } = useParams();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["event-detail", "public", eventId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/public/events/${eventId}/`);
      if (!res.data?.success)
        throw new Error("イベントの取得に失敗しました");
      return res.data.data;
    },
    // 404（非公開・存在しない）はリトライしない
    retry: (count, err) => err?.response?.status !== 404 && count < 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const notFound = isError && error?.response?.status === 404;
  const d = data ? parseDate(data.date) : null;

  return (
    <div className="pubDetail">
      <header className="pubDetailHdr">
        <div className="pubDetailBrand">
          <Link to="/top" className="pubDetailBrandLink">DOG SHOW CATALOGUE</Link>
        </div>
        <div className="pubDetailInner">
          <Link to="/top" className="pubDetailBack">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C8A35A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 6l-6 6 6 6" />
            </svg>
            <span>戻る</span>
          </Link>
          <div className="pubDetailHdrTitle">イベント</div>
          <span className="pubDetailHdrPad" />
        </div>
      </header>

      {isLoading ? (
        <div className="pubDetailMsg">読み込み中...</div>
      ) : isError ? (
        <div className="pubDetailMsg">
          {notFound
            ? "イベントが見つかりません"
            : `エラー: ${error?.message || "イベントの取得に失敗しました"}`}
          <br />
          <Link to="/top">イベント一覧へ戻る</Link>
        </div>
      ) : (
        <>
          <div className="pubDetailHero">
            <div className="pubDetailInner">
              {d && <div className="pubDetailEyebrow">{formatEyebrow(d)}</div>}
              <h1 className="pubDetailTitle">{data.title}</h1>
              <div className="pubDetailRule" />
            </div>
          </div>

          <div className="pubDetailInner">
            <div className="pubDetailInfos">
              <div className="pubDetailInfo">
                <span className="pubDetailKey">主催</span>
                <span className="pubDetailVal">{data.organizer}</span>
              </div>
              <div className="pubDetailInfo">
                <span className="pubDetailKey">開催日</span>
                <span className="pubDetailVal">{d ? formatDateJP(d) : ""}</span>
              </div>
              <div className="pubDetailInfo">
                <span className="pubDetailKey">会場</span>
                <span className="pubDetailVal">{data.venue}</span>
              </div>
              <div className="pubDetailInfo">
                <span className="pubDetailKey">出陳頭数</span>
                <span className="pubDetailVal">
                  <span className="pubDetailCount">{data.entry_count}</span> 頭
                </span>
              </div>
            </div>

            <nav className="pubMenu" aria-label="目録メニュー">
              {MENU.map((m) => (
                <Link key={m.sub} to={`/events/${eventId}/${m.path}`} className="pubMenuBtn">
                  <span className="pubMenuTxt">
                    <span className="pubMenuLabel">{m.label}</span>
                    <span className="pubMenuSub">{m.sub}</span>
                  </span>
                  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C8A35A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </Link>
              ))}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
